import type { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import type {
  VisibleIf,
  VisibleIfGroup,
  VisibleIfNot,
} from "@/types/api/spotflow/spotflow-config";

// Pure core of the VisibleIf evaluation, shared by the form-backed hook
// (useConfigVisibility), CorrectFieldWrapper, and form-less consumers like
// the canvas pinned-field strip. Callers supply how a rule's config_source
// resolves to a value (form.watch vs nodeConfig + defaults).
export type ConfigSourceValueResolver = (configSource: string) => unknown;

// Bindable bool controls store their literal as the string "true"/"false",
// whereas a VisibleIf rule's `value` is a real boolean. Normalize both sides so
// a bool toggled True→False keeps driving visibility (otherwise "false" !==
// false leaves the gated field stuck hidden).
function normalizeBoolish(v: unknown): unknown {
  if (v === "true") return true;
  if (v === "false") return false;
  return v;
}

export function evaluateVisibilityRule(
  rule: VisibleIf | VisibleIfNot | VisibleIfGroup,
  resolveValue: ConfigSourceValueResolver
): boolean {
  if (rule.type === "visible_if" || rule.type === "visible_if_not") {
    const visible_if = rule as VisibleIf;
    const raw = resolveValue(visible_if.config_source);
    // `??` (not `||`): a wrapped literal `false`/empty value must win over the
    // wrapper object, or bool rules comparing against false never match.
    const config_value = normalizeBoolish(
      (raw as { value?: unknown } | null | undefined)?.value ?? raw
    );
    const target = normalizeBoolish(visible_if.value);
    return rule.type === "visible_if"
      ? config_value === target
      : config_value !== target;
  }

  if (rule.type === "visible_if_group") {
    const visible_if_group = rule as VisibleIfGroup;
    if (visible_if_group.mode === "or") {
      return visible_if_group.conds.some((cond) =>
        evaluateVisibilityRule(cond, resolveValue)
      );
    }
    return visible_if_group.conds.every((cond) =>
      evaluateVisibilityRule(cond, resolveValue)
    );
  }

  return true;
}

export function isConfigEntryVisible(
  configItem: NoxusFormEntryConfig,
  resolveValue: ConfigSourceValueResolver
): boolean {
  if (!configItem.visible) return false;

  const shouldRenderField = configItem?.rules?.every((r) => {
    if (
      r.type === "visible_if" ||
      r.type === "visible_if_group" ||
      r.type === "visible_if_not"
    ) {
      return evaluateVisibilityRule(
        r as VisibleIf | VisibleIfNot | VisibleIfGroup,
        resolveValue
      );
    }
    return true;
  });

  return shouldRenderField ?? true;
}
